import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ClipboardList, RefreshCcw } from 'lucide-react';
import { callAdminGetAuditLogs } from '../../services/admin';
import { AuditLogFilterBar, emptyAuditFilters, filterAuditLogs, AuditFilterState } from './auditLogFilters';

type AuditLog = {
  id: string;
  eventType?: string;
  outcome?: string;
  target?: Record<string, unknown> | null;
  details?: Record<string, unknown> | null;
  createdAt?: any;
  receivedAt?: any;
};

const statusPill = (value: string) => {
  const v = (value || '').toLowerCase();
  if (v === 'success' || v === 'processed' || v === 'received') return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
  if (v === 'error' || v === 'rejected') return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
  return 'bg-slate-100 text-slate-700 dark:bg-gray-800 dark:text-gray-300';
};

const tsToMs = (ts: any) => {
  if (!ts) return null;
  if (typeof ts?.toMillis === 'function') return ts.toMillis();
  if (typeof ts?._seconds === 'number') return ts._seconds * 1000;
  const parsed = new Date(ts).getTime();
  return Number.isNaN(parsed) ? null : parsed;
};

const formatDate = (a: any, b: any) => {
  const ms = tsToMs(a) || tsToMs(b);
  if (!ms) return '—';
  return new Date(ms).toLocaleString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getText = (obj: Record<string, unknown> | null | undefined, key: string): string => {
  const value = obj?.[key];
  return value == null ? '' : String(value);
};

const AdminVetAxisAuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState(100);
  const [filters, setFilters] = useState<AuditFilterState>(emptyAuditFilters);
  const filteredLogs = useMemo(() => filterAuditLogs(logs, filters), [logs, filters]);

  const load = async (nextLimit = limit) => {
    setLoading(true);
    setError(null);
    try {
      const data = await callAdminGetAuditLogs({ productId: 'vetaxis', limit: nextLimit });
      setLogs(data.logs || []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load VetAxis audit logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="mb-8" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <Link to="/admin/vetaxis" className="inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:underline mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to VetAxis Ops
          </Link>
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary-500/10 dark:bg-primary-400/10">
              <ClipboardList className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white">VetAxis Audit Logs</h1>
              <p className="text-slate-600 dark:text-gray-400">Recent VetAxis-specific system and payment events</p>
            </div>
          </div>
        </motion.div>

        <motion.div className="glass-effect rounded-2xl p-4 shadow-glass mb-6" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <label className="text-sm text-slate-700 dark:text-gray-300 inline-flex items-center gap-2">
              <span>Limit</span>
              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="px-2 py-1 rounded-lg border border-slate-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/50"
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </select>
            </label>
            <button
              onClick={() => void load(limit)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 dark:border-gray-700 text-sm text-slate-700 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800"
            >
              <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <AuditLogFilterBar logs={logs} filters={filters} onChange={setFilters} shownCount={filteredLogs.length} />
          </div>
        </motion.div>

        {error && <div className="mb-4 text-sm text-red-600 dark:text-red-400">{error}</div>}

        <motion.div className="glass-effect rounded-2xl p-4 shadow-glass" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}>
          {loading ? (
            <p className="text-sm text-slate-500 dark:text-gray-400">Loading logs...</p>
          ) : filteredLogs.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-gray-400">
              {logs.length === 0 ? 'No VetAxis logs found.' : 'No logs match your filters.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 dark:text-gray-400 border-b border-slate-200 dark:border-gray-700">
                    <th className="py-2 pr-3">Date</th>
                    <th className="py-2 pr-3">Event Type</th>
                    <th className="py-2 pr-3">Outcome</th>
                    <th className="py-2 pr-3">UID</th>
                    <th className="py-2 pr-3">Summary</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.map((log) => (
                    <tr key={log.id} className="border-b border-slate-100 dark:border-gray-800">
                      <td className="py-2 pr-3">{formatDate(log.createdAt, log.receivedAt)}</td>
                      <td className="py-2 pr-3 font-mono text-xs">{log.eventType || '—'}</td>
                      <td className="py-2 pr-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${statusPill(log.outcome || 'info')}`}>{log.outcome || 'info'}</span>
                      </td>
                      <td className="py-2 pr-3 font-mono text-xs">{getText(log.target || null, 'uid') || getText(log.details || null, 'uid') || '—'}</td>
                      <td className="py-2 pr-3 text-slate-700 dark:text-gray-300">{getText(log.details || null, 'reason') || getText(log.details || null, 'paymentStatus') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminVetAxisAuditLogs;
